// ReportingSportConfig — the base class every sport extends.
//
// Sport-agnostic behaviour (display formatting) lives here as methods so it
// can reuse `this.unitSystem` / `this.periodLabel` without the caller having
// to thread them as args. Sports pass their static data to `super(...)`; any
// formatter can be overridden per sport by declaring a method on the subclass.
import type { ReportingSport } from '@frontend/core'
import type { PitchCoordinates } from '@frontend/core/lib/domains/pitches/types'
import type { UnitSystem } from '@frontend/core/lib/domains/units/types'
import { getOrdinal } from '@frontend/utils'
import type {
  ColumnDef,
  DashboardChartMetricDef,
  MonitorMetricDef,
  PlayCategoryDef,
  PositionFilterDef,
  TabGroupDef
} from './types'

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

/** The static, sport-specific data each sport hands to `super(...)`. */
export interface ReportingSportData<C extends string = string> {
  sport: ReportingSport
  displayName: string
  emoji: string
  themeClass: string
  periodLabel: 'Half' | 'Quarter'
  periodPluralLabel: 'Halves' | 'Quarters'
  pitch: { sport: string; coordinates: PitchCoordinates }
  positionFilters: PositionFilterDef<C>[]
  positionColors: Record<C, string>
  playCategories: PlayCategoryDef[]
  dashboard: {
    summaryMetricKeys: string[]
    topListKeys: string[]
    chartMetrics: DashboardChartMetricDef[]
  }
  playerPerformance: {
    staticColumns: ColumnDef[]
    summaryColumns: ColumnDef[]
    tabGroups: TabGroupDef[]
  }
  playerCompare: {
    staticColumns: ColumnDef[]
    summaryColumns: ColumnDef[]
    tabGroups: TabGroupDef[]
  }
  teamTable: {
    staticColumns: ColumnDef[]
    tabGroups: TabGroupDef[]
    chartCategories: string[]
  }
  monitoring: {
    physicalMetrics: MonitorMetricDef[]
    technicalMetrics:
      MonitorMetricDef[] | ((position: string | null) => MonitorMetricDef[])
  }
  resolvePosition: (input: string | null | undefined) => C | null
}

// Declaration merging: the interface makes the data fields look like direct
// properties on the class (so `config.sport` / `config.pitch` still works),
// while the constructor uses `Object.assign` to populate them at runtime.
export interface ReportingSportConfig<
  C extends string = string
> extends ReportingSportData<C> {}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export abstract class ReportingSportConfig<C extends string = string> {
  public readonly unitSystem: UnitSystem

  constructor(unitSystem: UnitSystem, data: ReportingSportData<C>) {
    this.unitSystem = unitSystem
    Object.assign(this, data)
  }

  // --- Formatters — bound to `this.unitSystem` + `this.periodLabel` ---

  /** Unit abbreviation for distances, e.g. "m" or "yd". */
  get distanceUnit(): string {
    return this.unitSystem.units.distance.abbreviation
  }

  /** Unit abbreviation for speeds, e.g. "kph" or "mph". */
  get speedUnit(): string {
    return this.unitSystem.units.speed.abbreviation
  }

  formatDuration(seconds: number): string {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = Math.floor(seconds % 60)
    return `${pad2(h)}:${pad2(m)}:${pad2(s)}`
  }

  /** "1st Half" / "3rd Quarter" / "Overtime". */
  formatPeriodName(periodNumber: number, overtime?: boolean): string {
    if (overtime) return 'Overtime'
    return `${getOrdinal(periodNumber)} ${this.periodLabel}`
  }
}
